import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Minus, Flame, Users, Sparkles, Check, X, Loader2 } from 'lucide-react';

const C = { navy: '#0B1E3D', blue: '#1A4C7C', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A' };
const FLAG = { alert: C.red, watch: C.amber, ok: C.green };
const fmtM = (v) => (v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${Math.round(v / 1e3)}K`);
const slug = (s) => s.replace(/[^a-z0-9]+/gi, '-').toLowerCase();

function ProposalPanel({ proposal, busy, onApply, onDismiss }) {
  const applied = proposal.status === 'applied';
  const dismissed = proposal.status === 'dismissed';
  const app = proposal.application || {};
  return (
    <div className="px-3 py-3 rounded-sm" data-testid={`aria-proposal-panel-${slug(proposal.class)}`}
      style={{ background: 'rgba(26,76,124,0.05)', border: '1px solid rgba(26,76,124,0.18)' }}>
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-3.5 h-3.5" style={{ color: C.blue }} />
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: C.blue }}>ARIA-APPETITE proposal</span>
        {applied && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm" style={{ background: 'rgba(29,158,117,0.12)', color: C.green }} data-testid="proposal-applied-badge">Applied</span>}
        {dismissed && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm" style={{ background: 'rgba(92,107,122,0.12)', color: C.slate }}>Dismissed</span>}
      </div>
      <p className="text-xs leading-relaxed mb-3" style={{ color: C.navy }} data-testid="proposal-rationale">{proposal.rationale}</p>
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-[11px] font-mono px-2 py-1 rounded-sm" style={{ background: '#FFF', border: '1px solid rgba(136,150,168,0.2)', color: C.navy }}>
          Rate loading <b style={{ color: C.red }}>+{proposal.proposed_rate_loading_pct}%</b>
        </div>
        <div className="text-[11px] font-mono px-2 py-1 rounded-sm" style={{ background: '#FFF', border: '1px solid rgba(136,150,168,0.2)', color: C.navy }}>
          Referral tightening <b style={{ color: C.red }}>{proposal.proposed_referral_tightening_pct}%</b>
        </div>
        {!applied && !dismissed && (
          <div className="ml-auto flex gap-2">
            <button onClick={onDismiss} disabled={busy} data-testid={`dismiss-proposal-${slug(proposal.class)}`}
              className="text-[11px] font-semibold px-3 py-1.5 rounded-sm flex items-center gap-1 disabled:opacity-50"
              style={{ color: C.slate, border: '1px solid rgba(136,150,168,0.3)', background: '#FFF' }}>
              <X className="w-3 h-3" /> Dismiss
            </button>
            <button onClick={onApply} disabled={busy} data-testid={`apply-proposal-${slug(proposal.class)}`}
              className="text-[11px] font-semibold px-3 py-1.5 rounded-sm flex items-center gap-1 text-white disabled:opacity-50"
              style={{ background: C.navy }}>
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Apply now
            </button>
          </div>
        )}
      </div>
      {applied && (
        <div className="mt-2 text-[11px]" style={{ color: C.slate }} data-testid="proposal-application-summary">
          {app.rate_loading?.applied
            ? <>Rates loaded +{app.rate_loading.loading_pct}% ({app.rate_loading.fields_scaled} base-rate field{app.rate_loading.fields_scaled !== 1 ? 's' : ''}, rate table v{app.rate_loading.rate_version}). </>
            : <>Rate loading skipped — {app.rate_loading?.reason}. </>}
          {app.referral_tightening?.applied
            ? <>STP band tightened{app.referral_tightening.band ? ` — auto-issue now ≤ ${app.referral_tightening.band.auto_issue_max_score}` : ''}.</>
            : <>Referral tightening skipped — {app.referral_tightening?.reason}.</>}
          <span className="ml-1">Applied by {proposal.applied_by} · fully audited.</span>
        </div>
      )}
    </div>
  );
}

function WatchTable({ title, icon: Icon, rows, testid, aria }) {
  return (
    <Card className="p-4 rounded-sm" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color: C.blue }} />
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead><tr className="border-b" style={{ borderColor: 'rgba(136,150,168,0.2)' }}>
            {['', 'Name', 'Active GWP', 'Claims 90d', 'Incurred 90d', 'LR 90d', 'Trend', '', aria ? 'ARIA' : null].filter(h => h !== null).map((h, i) => (
              <th key={i} className="py-1.5 pr-3 text-[9px] uppercase tracking-wider font-bold" style={{ color: C.slate }}>{h}</th>))}
          </tr></thead>
          <tbody>
            {rows.map(r => {
              const TrendIcon = r.trend_pts > 2 ? TrendingUp : r.trend_pts < -2 ? TrendingDown : Minus;
              const trendColor = r.trend_pts > 2 ? C.red : r.trend_pts < -2 ? C.green : C.slate;
              const prop = aria?.proposals?.[r.label];
              const isOpen = aria?.expanded === r.label;
              const busy = aria?.busy === r.label;
              return (
                <React.Fragment key={r.label}>
                  <tr className="border-b" style={{ borderColor: 'rgba(136,150,168,0.08)' }}
                    data-testid={`watch-row-${slug(r.label)}`}>
                    <td className="py-1.5 pr-2"><span className="w-2 h-2 rounded-full inline-block" style={{ background: FLAG[r.flag] }} /></td>
                    <td className="py-1.5 pr-3 text-xs font-medium" style={{ color: C.navy }}>{r.label}</td>
                    <td className="py-1.5 pr-3 text-xs font-mono" style={{ color: C.slate }}>{fmtM(r.gwp)}</td>
                    <td className="py-1.5 pr-3 text-xs font-mono" style={{ color: C.slate }}>{r.claims_90d}</td>
                    <td className="py-1.5 pr-3 text-xs font-mono" style={{ color: C.navy }}>{fmtM(r.incurred_90d)}</td>
                    <td className="py-1.5 pr-3 text-xs font-mono font-bold" style={{ color: FLAG[r.flag] }}>{r.lr_90d_pct}%</td>
                    <td className="py-1.5 pr-3 text-xs font-mono">
                      <span className="flex items-center gap-1" style={{ color: trendColor }}>
                        <TrendIcon className="w-3 h-3" />{r.trend_pts > 0 ? '+' : ''}{r.trend_pts}pts
                      </span>
                    </td>
                    <td className="py-1.5 pr-3">
                      <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm" style={{ background: `${FLAG[r.flag]}1A`, color: FLAG[r.flag] }}>{r.flag}</span>
                    </td>
                    {aria && (
                      <td className="py-1.5">
                        {r.flag !== 'ok' && (
                          <button onClick={() => aria.onPropose(r.label)} disabled={busy}
                            data-testid={`aria-proposal-btn-${slug(r.label)}`}
                            className="text-[10px] font-semibold px-2 py-1 rounded-sm flex items-center gap-1 whitespace-nowrap disabled:opacity-50 transition-colors"
                            style={prop?.status === 'applied'
                              ? { background: 'rgba(29,158,117,0.1)', color: C.green, border: '1px solid rgba(29,158,117,0.3)' }
                              : { background: 'rgba(26,76,124,0.08)', color: C.blue, border: '1px solid rgba(26,76,124,0.25)' }}>
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                            {prop?.status === 'applied' ? 'Applied' : prop ? (isOpen ? 'Hide' : 'View proposal') : 'ARIA proposal'}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  {aria && isOpen && prop && (
                    <tr className="border-b" style={{ borderColor: 'rgba(136,150,168,0.08)' }}>
                      <td colSpan={9} className="py-2">
                        <ProposalPanel proposal={prop} busy={busy}
                          onApply={() => aria.onApply(prop)} onDismiss={() => aria.onDismiss(prop)} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function LossWatchlistPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proposals, setProposals] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    Promise.all([api.get('/analytics/loss-watchlist'), api.get('/analytics/appetite-proposals')])
      .then(([wl, pr]) => {
        setData(wl.data);
        const map = {};
        (pr.data.proposals || []).forEach(p => { if (!map[p.class]) map[p.class] = p; });
        setProposals(map);
      })
      .catch(e => console.error(e?.message)).finally(() => setLoading(false));
  }, [api]);

  const upsert = useCallback((p) => setProposals(prev => ({ ...prev, [p.class]: p })), []);

  const onPropose = async (label) => {
    if (busy) return;
    if (proposals[label]) { setExpanded(expanded === label ? null : label); return; }
    setBusy(label);
    try {
      const r = await api.post('/analytics/appetite-proposals', { class: label });
      if (r.data.proposal) { upsert(r.data.proposal); setExpanded(label); }
      else toast.info(r.data.reason || 'No action needed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate proposal');
    } finally { setBusy(null); }
  };

  const onApply = async (prop) => {
    if (busy) return;
    setBusy(prop.class);
    try {
      const r = await api.post(`/analytics/appetite-proposals/${prop.proposal_id}/apply`);
      upsert(r.data.proposal);
      toast.success(`Appetite tightened for ${prop.class} — rates loaded and STP band adjusted`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply proposal');
    } finally { setBusy(null); }
  };

  const onDismiss = async (prop) => {
    if (busy) return;
    setBusy(prop.class);
    try {
      const r = await api.post(`/analytics/appetite-proposals/${prop.proposal_id}/dismiss`);
      upsert(r.data.proposal);
      setExpanded(null);
      toast.info(`Proposal dismissed for ${prop.class}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to dismiss proposal');
    } finally { setBusy(null); }
  };

  if (loading) return <div className="space-y-3">{[1, 2].map(i => <Skeleton key={i} className="h-32 w-full" />)}</div>;
  if (!data) return <p className="text-sm" style={{ color: C.slate }}>Watchlist unavailable.</p>;

  return (
    <div className="space-y-4" data-testid="loss-watchlist-page">
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: C.navy, fontFamily: 'Georgia, serif' }}>Loss Ratio Watchlist</h1>
          <p className="text-sm mt-1" style={{ color: C.slate }}>{data.window} · flags: alert ≥100% LR or ≥70% and rising · watch ≥70% LR or +15pts trend</p>
        </div>
        <span className="ml-auto text-[11px] font-semibold px-2.5 py-1 rounded-sm" data-testid="watchlist-flag-count"
          style={{ background: data.flagged ? 'rgba(226,75,74,0.1)' : 'rgba(29,158,117,0.1)', color: data.flagged ? C.red : C.green }}>
          {data.flagged ? `${data.flagged} flagged` : 'Book healthy — nothing flagged'}
        </span>
      </div>
      <WatchTable title="By Insurance Class" icon={Flame} rows={data.classes} testid="watchlist-classes"
        aria={{ proposals, expanded, busy, onPropose, onApply, onDismiss }} />
      <WatchTable title="By Brokerage (top 20 by risk)" icon={Users} rows={data.brokers} testid="watchlist-brokers" />
    </div>
  );
}
